/**
 * PanelList — an ordered list of records, one inline row each, with add and remove.
 *
 * The ground layers are the case: a band is a material and a depth, there are two of them by
 * default and no reason there should be exactly two.
 *
 * ONE ROW PER ITEM, NOT ONE DISCLOSURE PER ITEM. Two fields behind a chevron is a tap to reach a
 * material and a number that both fit on the line the chevron is sitting on — the disclosure was
 * costing more than it hid.
 *
 * IT KNOWS NOTHING ABOUT WHAT A ROW CONTAINS. The caller passes rendered controls; the schema lives
 * with the renderer that has one, not in the kit.
 */

import type { ReactNode } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import { MOTION } from './tokens';

interface PanelListProps {
  rows: { key: string; controls: ReactNode }[];
  onAdd?: () => void;
  onRemove?: (index: number) => void;
  addLabel?: string;
  /** Rows below this cannot be removed — a list of none is usually not a legal config. */
  minRows?: number;
}

export function PanelList({ rows, onAdd, onRemove, addLabel = 'Add', minRows = 1 }: PanelListProps) {
  return (
    <div className="flex flex-col gap-1">
      {rows.map((row, i) => (
        <div key={row.key} className="flex items-center gap-1.5">
          <div className="flex-1 min-w-0 flex items-center gap-1.5">{row.controls}</div>
          {onRemove && (
            <button
              type="button"
              aria-label={`Remove ${i + 1}`}
              disabled={rows.length <= minRows}
              onClick={() => onRemove(i)}
              className={`w-6 h-6 shrink-0 grid place-items-center rounded-full text-neutral-400 ${MOTION} ${
                rows.length <= minRows ? 'opacity-30' : 'cursor-pointer hover:bg-neutral-100 hover:text-neutral-700'
              }`}
            >
              <Trash2 size={12} />
            </button>
          )}
        </div>
      ))}
      {onAdd && (
        <button
          type="button"
          onClick={onAdd}
          className={`self-start flex items-center gap-1 px-2.5 h-7 rounded-full bg-neutral-100
            text-[11px] font-bold text-neutral-500 cursor-pointer hover:text-neutral-800 ${MOTION}`}
        >
          <Plus size={12} /> {addLabel}
        </button>
      )}
    </div>
  );
}
